import { apiClient, getAuthToken } from "./client";
import type { ThreadV4LayoutJson, ThreadV4WidgetPosition, ThreadV4WidgetSpec } from "./threadV4Views";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";
const REALTIME_BASE = import.meta.env.VITE_REALTIME_URL ?? API_BASE;

export type ThreadRealtimeStatus = "idle" | "connecting" | "connected" | "auth-missing" | "error";

export interface ThreadPresenceState {
  user_id: string;
  display_name?: string | null;
  view_id?: string | null;
  cursor?: Record<string, unknown> | null;
  selection?: Record<string, unknown> | null;
  last_seen?: string | null;
}

export interface ThreadChatChannel {
  id: string;
  project_id: string;
  kind: "project" | "document" | "direct";
  subject_id: string | null;
  name: string | null;
  created_at: string;
  created_by: string;
  archived_at: string | null;
}

export interface ThreadChatMessage {
  id: string;
  channel_id: string;
  author_id: string;
  body: string;
  body_format: string;
  reply_to_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
  pinned_at?: string | null;
  pinned_by?: string | null;
}

export interface ThreadTypingEvent {
  type: "typing";
  user_id: string;
  display_name?: string | null;
  channel_id: string;
  started_at: string;
  expires_at: string;
}

export interface ThreadDashboardLayoutSnapshot {
  type: "snapshot";
  view_id: string;
  document_id: string;
  project_id: string;
  layout_json: ThreadV4LayoutJson;
  revision: string | null;
  updated_at: string | null;
  access: "read" | "write";
}

export type ThreadDashboardLayoutOperationType =
  | "layout.replace"
  | "layout.widget.upsert"
  | "layout.widget.move"
  | "layout.widget.resize"
  | "layout.widget.remove";

export interface ThreadDashboardLayoutOperationEvent {
  type: ThreadDashboardLayoutOperationType;
  view_id: string;
  document_id: string;
  project_id: string;
  op_id?: string | null;
  widget_id?: string | null;
  author_id: string;
  author_label?: string | null;
  layout_json: ThreadV4LayoutJson;
  revision: string | null;
  updated_at: string | null;
}

export type ThreadDashboardLayoutReplaceEvent = ThreadDashboardLayoutOperationEvent;

export type ThreadDashboardLayoutOutboundMessage =
  | { type: "layout.replace"; layout_json: ThreadV4LayoutJson }
  | { type: "layout.widget.upsert"; widget: ThreadV4WidgetSpec }
  | { type: "layout.widget.move" | "layout.widget.resize"; widget_id: string; position: ThreadV4WidgetPosition }
  | { type: "layout.widget.remove"; widget_id: string };

export interface ThreadDashboardEditingEvent {
  type: "editing";
  view_id: string;
  document_id: string;
  user_id: string;
  display_name?: string | null;
  widget_id?: string | null;
  mode?: string | null;
  started_at: string;
  expires_at?: string | null;
}

export interface ThreadRealtimeErrorEvent {
  type: "error";
  code?: string;
  detail?: string;
}

export type ThreadDashboardLayoutStreamEvent =
  | ThreadDashboardLayoutSnapshot
  | ThreadDashboardLayoutOperationEvent
  | ThreadDashboardEditingEvent
  | ThreadRealtimeErrorEvent
  | { type: "ping" | "pong"; ts?: string };

function realtimeUrl(path: string, token: string): string {
  const base = new URL(REALTIME_BASE.endsWith("/") ? REALTIME_BASE : `${REALTIME_BASE}/`, window.location.origin);
  const url = new URL(path.replace(/^\//, ""), base);
  if (url.protocol === "https:") url.protocol = "wss:";
  if (url.protocol === "http:") url.protocol = "ws:";
  url.searchParams.set("token", token);
  return url.toString();
}

export async function getRealtimeAuthToken(): Promise<string | null> {
  return getAuthToken();
}

export function presenceWebSocketUrl(projectId: string, token: string): string {
  return realtimeUrl(`/projects/${projectId}/presence`, token);
}

export function chatWebSocketUrl(channelId: string, token: string): string {
  return realtimeUrl(`/channels/${channelId}/stream`, token);
}

export function dashboardLayoutWebSocketUrl(viewId: string, token: string): string {
  return realtimeUrl(`/v4/views/${viewId}/layout-stream`, token);
}

export async function fetchChatChannels(projectId: string): Promise<ThreadChatChannel[]> {
  return apiClient<ThreadChatChannel[]>(`/projects/${projectId}/channels`);
}

export async function ensureProjectChatChannel(projectId: string, name = "Project chat"): Promise<ThreadChatChannel> {
  const channels = await fetchChatChannels(projectId);
  const projectChannel = channels.find((channel) => channel.kind === "project" && channel.subject_id === null);
  if (projectChannel) return projectChannel;
  return apiClient<ThreadChatChannel>(`/projects/${projectId}/channels`, {
    method: "POST",
    body: { kind: "project", subject_id: null, name },
  });
}

export async function fetchChatMessages(channelId: string, limit = 50): Promise<ThreadChatMessage[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  const messages = await apiClient<ThreadChatMessage[]>(`/channels/${channelId}/messages?${params.toString()}`);
  return [...messages].reverse();
}

export async function sendChatMessage(channelId: string, body: string): Promise<ThreadChatMessage> {
  return apiClient<ThreadChatMessage>(`/channels/${channelId}/messages`, {
    method: "POST",
    body: { body, body_format: "markdown" },
  });
}

export async function signalChatTyping(channelId: string): Promise<void> {
  await apiClient<void>(`/channels/${channelId}/typing`, { method: "POST" });
}
